// src/hooks/useIdle.ts
import { useState, useEffect, useRef } from 'react';

/**
 * A custom hook to detect when the user is idle.
 * @param timeout The amount of time in milliseconds before the user is considered idle.
 * @returns An object containing the `isIdle` state and a function to manually set it.
 */
export function useIdle({ timeout }: { timeout: number }) {
    const [isIdle, setIsIdle] = useState(false);
    const idleTimer = useRef<number | null>(null);

    // This function is called on any user activity.
    const resetTimer = () => {
        // 1. When activity occurs, the user is no longer idle.
        setIsIdle(false);

        // 2. Clear any previous timer that was set.
        if (idleTimer.current) {
            clearTimeout(idleTimer.current);
        }

        // 3. Set a new timer. If this timer completes without being reset, the user is considered idle.
        idleTimer.current = window.setTimeout(() => {
            setIsIdle(true);
        }, timeout);
    };

    useEffect(() => {
        // Start the timer when the component mounts.
        resetTimer();

        // Listen for common user activity events.
        // REMOVED 'mousemove', 'scroll' from events.
        const events = ['keydown', 'mousedown', 'touchstart'];
        events.forEach(event => window.addEventListener(event, resetTimer, { passive: true }));

        // Cleanup function to remove timers and listeners when the component unmounts.
        return () => {
            if (idleTimer.current) {
                clearTimeout(idleTimer.current);
            }
            events.forEach(event => window.removeEventListener(event, resetTimer));
        };
    }, [timeout]); // The effect only re-runs if the timeout value changes.

    return { isIdle, setIsIdle };
}